import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Refresh } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { Page, PageHeader } from "../../components/layout/Page";
import { fetchGeneralItemCategories, fetchGeneralItems } from "../../api/priceTables";
import { useAdminCrud } from "../../hooks/useAdminCrud";
import { useDebounce } from "../../hooks/useDebounce";
import {
  ActionsHeadCell,
  DataCard,
  DataPagination,
  DataToolbar,
  RowActions,
  SearchField,
  TableMessageRow,
} from "./DataTableParts";
import { FORM_GRID_SX, FULL_SPAN_SX, TIGHT_COL_SX, formatMoney } from "./dataTableStyles";

// The unified curated_item_prices table: human-authored prices for everything
// that has no dedicated curated table (ribbons, audio, connectors, stationery, ...).

interface GeneralItem {
  id: number;
  category: string;
  brand: string | null;
  model: string | null;
  normalized_model: string | null;
  /** numeric: PostgREST may send it as a string. */
  price: number | string;
  price_unit: string | null;
  keywords: string[] | null;
  specs: Record<string, unknown> | null;
  notes: string | null;
  /** DBR does not normally supply this; the row records that it was sent at $0. */
  not_offered?: boolean | null;
  created_at: string;
  updated_at: string;
}

interface GeneralItemsResponse {
  items: GeneralItem[];
  total: number;
  page: number;
  limit: number;
}

interface GeneralItemForm {
  category: string;
  brand: string;
  model: string;
  price: string;
  price_unit: string;
  keywords: string;
  notes: string;
  not_offered: boolean;
}

/** What a save sends: only the columns the dialog edits, so specs and timestamps are left alone. */
type GeneralItemPayload = {
  category: string;
  brand: string | null;
  model: string | null;
  normalized_model?: string;
  price: number;
  price_unit: string;
  keywords: string[];
  notes: string | null;
  not_offered?: boolean;
};

const emptyForm: GeneralItemForm = {
  category: "",
  brand: "",
  model: "",
  price: "",
  price_unit: "each",
  keywords: "",
  notes: "",
  not_offered: false,
};

const PRICE_UNITS = ["each", "per_meter", "per_set", "per_pack", "per_box", "per_roll"];
/** The only category whose rows the Not offered switch applies to. */
const STATIONERY_CATEGORY = "stationery";
// Category, Brand, Model, Price, Unit, Keywords, Notes, Actions
const COL_COUNT = 8;

const splitKeywords = (text: string) =>
  text
    .split(",")
    .map((k) => k.trim())
    .filter((k) => k);

/** Same recipe as scripts/lib/curatedRowBuilder.js deriveNormalizedModel, which seeded the table. */
function normalizedModelFor(brand: string, model: string, keywords: string[]): string {
  const parts = [brand, model].map((p) => p.trim()).filter((p) => p);
  if (parts.length === 0) parts.push(...keywords.filter((k) => k.length >= 2).slice(0, 5));
  return parts.join(" ").toLowerCase().replace(/[^a-z0-9]/g, "");
}

const formatCategory = (category: string) =>
  category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatUnit = (unit: string | null) => (unit || "each").replace(/_/g, " ");

/** A one-line cell that shows the rest in a tooltip when it is cut off. */
function TruncatedText({ text, maxWidth }: { text: string | null; maxWidth: number }) {
  if (!text) {
    return (
      <Box component="span" sx={{ color: "text.disabled" }}>
        -
      </Box>
    );
  }
  return (
    <Tooltip title={text} placement="bottom-start" enterDelay={300}>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth }}>
        {text}
      </Typography>
    </Tooltip>
  );
}

export default function GeneralItemsManagement() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [editDialog, setEditDialog] = useState<{ open: boolean; item: GeneralItem | null }>({
    open: false,
    item: null,
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item: GeneralItem | null }>({
    open: false,
    item: null,
  });
  const [formData, setFormData] = useState<GeneralItemForm>(emptyForm);
  const [isAddMode, setIsAddMode] = useState(false);

  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, categoryFilter]);

  const {
    data: itemsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["generalItems", page, rowsPerPage, debouncedSearch, categoryFilter],
    queryFn: async (): Promise<GeneralItemsResponse> => {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (categoryFilter) params.append("category", categoryFilter);
      return fetchGeneralItems(params);
    },
    staleTime: 30000,
    placeholderData: (previousData) => previousData,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["generalItemCategories"],
    queryFn: async (): Promise<string[]> => {
      const data = await fetchGeneralItemCategories();
      return data?.categories ?? [];
    },
    staleTime: 60000,
  });

  const { createMutation, updateMutation, deleteMutation } = useAdminCrud<GeneralItemPayload>({
    endpoint: "/curated-item-prices",
    label: "general item",
    invalidate: ["generalItems"],
    createAlsoInvalidates: [["generalItemCategories"]],
    onSaved: () => handleCloseDialog(),
    onDeleted: () => setDeleteDialog({ open: false, item: null }),
  });

  // The switch applies to stationery rows only; any other row keeps what it has stored.
  const isStationery = formData.category.trim() === STATIONERY_CATEGORY;
  const notOffered = isStationery ? formData.not_offered : editDialog.item?.not_offered === true;

  const handleOpenEditDialog = (item: GeneralItem | null) => {
    setIsAddMode(!item);
    if (item) {
      setFormData({
        category: item.category,
        brand: item.brand || "",
        model: item.model || "",
        price: String(Number(item.price)),
        price_unit: item.price_unit || "each",
        keywords: item.keywords?.join(", ") || "",
        notes: item.notes || "",
        not_offered: item.not_offered === true,
      });
    } else {
      setFormData(emptyForm);
    }
    setEditDialog({ open: true, item });
  };

  const handleCloseDialog = () => {
    setEditDialog({ open: false, item: null });
    setFormData(emptyForm);
    setIsAddMode(false);
  };

  const handleSave = () => {
    const original = editDialog.item;
    // An untouched keyword field sends the stored array back exactly, so a keyword
    // that itself contains a comma is not split by the round trip.
    const keywords =
      original && formData.keywords === (original.keywords?.join(", ") || "")
        ? original.keywords ?? []
        : splitKeywords(formData.keywords);
    const brand = formData.brand.trim();
    const model = formData.model.trim();
    const data: GeneralItemPayload = {
      category: formData.category.trim(),
      brand: brand || null,
      model: model || null,
      // Kept while not offered (never served then), so switching back restores it.
      price: formData.price.trim() === "" ? 0 : Number(formData.price),
      price_unit: formData.price_unit || "each",
      keywords,
      notes: formData.notes.trim() || null,
    };
    // Sent only when it is or was set, so ordinary saves never name the column
    // (a save that names a column PostgREST does not know is refused).
    if (notOffered || original?.not_offered === true) {
      data.not_offered = notOffered;
    }
    // The matcher reads normalized_model; derive it for a new row, and keep the
    // stored one unless the brand or model it was derived from changed.
    if (!original || brand !== (original.brand || "") || model !== (original.model || "")) {
      data.normalized_model = normalizedModelFor(brand, model, keywords);
    }

    if (isAddMode) {
      createMutation.mutate(data);
    } else if (original) {
      updateMutation.mutate({ id: original.id, data });
    }
  };

  const handleDelete = () => {
    if (deleteDialog.item) deleteMutation.mutate(deleteDialog.item.id);
  };

  const items = itemsData?.items ?? [];
  const totalCount = itemsData?.total ?? 0;
  const showLoadingOverlay = isLoading && !itemsData;
  const saving = createMutation.isPending || updateMutation.isPending;
  const priceValid = notOffered || (formData.price.trim() !== "" && Number.isFinite(Number(formData.price)));
  const canSave = formData.category.trim() !== "" && priceValid && !saving;

  return (
    <Page width="full">
      <PageHeader
        title="General Items"
        subtitle="Curated prices for items without a dedicated price table (ribbons, audio, connectors, stationery and more)"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenEditDialog(null)}>
              Add New
            </Button>
          </>
        }
      />
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ mt: -1.5, mb: 2, maxWidth: "100ch" }}
      >
        Stationery rows match new RFQ lines that name one of their keywords. The Not offered switch is
        for stationery rows: marking one Not offered makes matching new RFQ lines arrive at $0.00 for you
        to validate; turning it off lets them be priced from the row or normally.
      </Typography>

      <DataCard>
        <DataToolbar
          summary={itemsData ? `${totalCount.toLocaleString()} ${totalCount === 1 ? "item" : "items"}` : undefined}
        >
          <SearchField
            placeholder="Search brand, model or notes..."
            value={searchInput}
            onChange={setSearchInput}
            busy={isFetching}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="general-items-category-label">Category</InputLabel>
            <Select
              labelId="general-items-category-label"
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {formatCategory(cat)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DataToolbar>

        {error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load general items: {(error as Error).message}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={TIGHT_COL_SX}>Category</TableCell>
                    <TableCell sx={TIGHT_COL_SX}>Brand</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="right" sx={TIGHT_COL_SX}>
                      Price
                    </TableCell>
                    <TableCell sx={TIGHT_COL_SX}>Unit</TableCell>
                    <TableCell>Keywords</TableCell>
                    <TableCell>Notes</TableCell>
                    <ActionsHeadCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {showLoadingOverlay ? (
                    <TableMessageRow colSpan={COL_COUNT} loading />
                  ) : items.length === 0 ? (
                    <TableMessageRow colSpan={COL_COUNT}>
                      No general items found.{" "}
                      {searchInput || categoryFilter
                        ? "Try adjusting your search criteria."
                        : 'Click "Add New" to add your first entry.'}
                    </TableMessageRow>
                  ) : (
                    items.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell sx={TIGHT_COL_SX}>
                          <Chip label={formatCategory(item.category)} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={TIGHT_COL_SX}>{item.brand || "-"}</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {item.model || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={TIGHT_COL_SX}>
                          {item.not_offered === true ? (
                            <Chip label="Not offered" size="small" color="warning" variant="outlined" />
                          ) : (
                            formatMoney(Number(item.price))
                          )}
                        </TableCell>
                        <TableCell sx={{ ...TIGHT_COL_SX, color: "text.secondary" }}>
                          {formatUnit(item.price_unit)}
                        </TableCell>
                        <TableCell>
                          <TruncatedText
                            text={item.keywords?.length ? item.keywords.join(", ") : null}
                            maxWidth={260}
                          />
                        </TableCell>
                        <TableCell>
                          <TruncatedText text={item.notes} maxWidth={360} />
                        </TableCell>
                        <RowActions
                          onEdit={() => handleOpenEditDialog(item)}
                          onDelete={() => setDeleteDialog({ open: true, item })}
                        />
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {totalCount > 0 && (
              <DataPagination
                count={totalCount}
                page={page}
                onPageChange={setPage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(n) => {
                  setRowsPerPage(n);
                  setPage(0);
                }}
              />
            )}
          </>
        )}
      </DataCard>

      {/* Add / edit dialog */}
      <Dialog open={editDialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isAddMode ? "Add General Item" : "Edit General Item"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={FORM_GRID_SX}>
            <Autocomplete
              freeSolo
              options={categories}
              inputValue={formData.category}
              onInputChange={(_, value) => setFormData((f) => ({ ...f, category: value }))}
              renderInput={(params) => (
                <TextField {...params} label="Category" required size="small" fullWidth />
              )}
              sx={FULL_SPAN_SX}
            />
            <TextField
              label="Brand"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              size="small"
              fullWidth
            />
            <TextField
              label="Model"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              size="small"
              fullWidth
            />
            <TextField
              label="Price"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              type="number"
              required={!notOffered}
              helperText={notOffered ? "Kept, but not used while Not offered" : undefined}
              size="small"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="general-item-unit-label">Unit</InputLabel>
              <Select
                labelId="general-item-unit-label"
                value={formData.price_unit}
                label="Unit"
                onChange={(e) => setFormData({ ...formData, price_unit: e.target.value })}
              >
                {[...new Set([...PRICE_UNITS, formData.price_unit])].map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {formatUnit(unit)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isStationery && (
              <Box sx={FULL_SPAN_SX}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.not_offered}
                      onChange={(e) => setFormData((f) => ({ ...f, not_offered: e.target.checked }))}
                    />
                  }
                  label="Not offered"
                />
                <Typography variant="caption" color="text.secondary" component="p">
                  DBR does not normally supply this item. Matching new RFQ lines arrive at $0.00 for you to
                  validate.
                </Typography>
              </Box>
            )}
            <TextField
              label="Keywords"
              helperText="Comma-separated"
              value={formData.keywords}
              onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
              size="small"
              fullWidth
              sx={FULL_SPAN_SX}
            />
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              size="small"
              fullWidth
              multiline
              minRows={2}
              sx={FULL_SPAN_SX}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!canSave}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>
              {[deleteDialog.item?.brand, deleteDialog.item?.model].filter(Boolean).join(" ") ||
                deleteDialog.item?.category}
            </strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
